import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/BranchFundDashboard.jsx
// Branch-scoped fund dashboard: cash on hand, cash in / out / reversals for a
// chosen business day, plus the branch's recent cash-drawer activity.
// Branch users see only their own branch; HO users can pick any branch.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, DatePicker, Select, Button, Typography, Tag,
  Table, Tooltip, Spin, Alert, Space, Empty, Divider,
} from 'antd';
import {
  ReloadOutlined, WalletOutlined, ArrowUpOutlined, ArrowDownOutlined,
  BankOutlined, InfoCircleOutlined, RollbackOutlined, ShopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchCashLedger, fetchCashLedgerBalance } from '../api';
import { CASH_LEDGER_ENTRY_TYPE, TREASURY_MODULE, DAY_STATUS_OPTIONS } from '../constants/treasuryConstants';

const { Title, Text } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const MODULE_LABELS = {
  [TREASURY_MODULE.WALK_IN]: 'Walk-In',
  [TREASURY_MODULE.PLEDGE_ADVANCE]: 'Pledge Advance',
  [TREASURY_MODULE.PLEDGE_SETTLEMENT]: 'Pledge Settlement',
  [TREASURY_MODULE.MANUAL_ADJUSTMENT]: 'Adjustment',
  [TREASURY_MODULE.BRANCH_FUNDING]: 'Branch Funding',
  [TREASURY_MODULE.INTER_BRANCH_TRANSFER]: 'Inter-Branch',
  [TREASURY_MODULE.EXPENSE]: 'Expense',
  [TREASURY_MODULE.EOD]: 'EOD',
};

const ENTRY_TYPE_COLORS = {
  [CASH_LEDGER_ENTRY_TYPE.OPENING_BALANCE]: 'blue',
  [CASH_LEDGER_ENTRY_TYPE.CASH_IN]: 'green',
  [CASH_LEDGER_ENTRY_TYPE.CASH_OUT]: 'red',
  [CASH_LEDGER_ENTRY_TYPE.ADJUSTMENT]: 'orange',
  [CASH_LEDGER_ENTRY_TYPE.CLOSING_BALANCE]: 'default',
};

const INFLOW_TYPES = [
  CASH_LEDGER_ENTRY_TYPE.OPENING_BALANCE,
  CASH_LEDGER_ENTRY_TYPE.CASH_IN,
  CASH_LEDGER_ENTRY_TYPE.ADJUSTMENT,
];

// A CASH_IN under a customer-payout module is a reversal (payout returned to the
// drawer), not genuine new cash in — matches the backend summary classification.
const CASH_PAYOUT_MODULES = new Set([
  TREASURY_MODULE.WALK_IN,
  TREASURY_MODULE.PLEDGE_SETTLEMENT,
  TREASURY_MODULE.PLEDGE_ADVANCE,
]);
const isPayoutReversal = (row) =>
  row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_IN && CASH_PAYOUT_MODULES.has(row.module);

const dayStatusMeta = (status) =>
  DAY_STATUS_OPTIONS.find((o) => o.value === status) || { label: status, color: 'default' };

export default function BranchFundDashboard() {
  const { user } = useAuth();

  const [userBranch, setUserBranch] = useState(null);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);   // { dayStatus, runningBalance, openingCash, summary }
  const [entries, setEntries] = useState([]);

  // ── Init: resolve branch type ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);

        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);

        if (ho) {
          const bRes = await fetchBranches({ limit: 1000 });
          setBranches(bRes?.data?.data || bRes?.data || []);
          // Default to the user's own (HO) branch if it is a real cash branch, else leave empty.
          setSelectedBranchId(null);
        } else {
          setSelectedBranchId(user.branch);
        }
      } catch (err) {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch dashboard data for the selected branch + day ───────────────────────
  const load = useCallback(async () => {
    const branchId = selectedBranchId || (isHO ? null : user?.branch);
    if (!branchId) { setBalance(null); setEntries([]); return; }

    setLoading(true);
    setError(null);
    try {
      const businessDate = selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined;
      const [balRes, ledgerRes] = await Promise.all([
        fetchCashLedgerBalance({ branchId, businessDate }),
        fetchCashLedger({ branchId, businessDate, limit: 100 }),
      ]);
      setBalance(balRes?.data?.data || null);
      setEntries(ledgerRes?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load branch fund dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId, selectedDate, isHO, user]);

  useEffect(() => {
    if (!initLoading) load();
  }, [initLoading, load]);

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const summary = balance?.summary || {};
  const cashOnHand = balance?.runningBalance ?? summary.closingBalance ?? 0;
  const reversals = summary.totalReversals || 0;
  const netToday = (summary.totalCashIn || 0) - (summary.totalCashOut || 0) + reversals;
  const status = balance?.dayStatus;
  const statusMeta = status ? dayStatusMeta(status) : null;
  const effectiveBranchId = selectedBranchId || (isHO ? null : user?.branch);
  const branchName = isHO
    ? (branches.find((b) => b._id === selectedBranchId)?.name)
    : userBranch?.name;

  const columns = [
    { title: 'Time', dataIndex: 'createdAt', width: 90, render: (v) => v ? dayjs(v).format('HH:mm') : '—' },
    { title: 'Reference', dataIndex: 'reference', width: 130, render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Module', dataIndex: 'module', width: 130, render: (v) => <Tag>{MODULE_LABELS[v] || v}</Tag> },
    {
      title: 'Type', dataIndex: 'entryType', width: 120,
      render: (v, row) => isPayoutReversal(row)
        ? <Tag color="purple">Reversal</Tag>
        : <Tag color={ENTRY_TYPE_COLORS[v]}>{(v || '').replace(/_/g, ' ')}</Tag>,
    },
    { title: 'Description', dataIndex: 'description', ellipsis: true, render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : <Text type="secondary">—</Text> },
    {
      title: 'Cash In', dataIndex: 'amount', width: 120, align: 'right',
      render: (v, row) => {
        if (!INFLOW_TYPES.includes(row.entryType)) return <Text type="secondary">—</Text>;
        if (isPayoutReversal(row)) {
          return <Tooltip title="Reversal — payout returned to the drawer, not new Cash In."><Text style={{ color: '#722ed1', fontWeight: 500 }}>↩ ₹{fmt(v)}</Text></Tooltip>;
        }
        return <Text style={{ color: '#389e0d', fontWeight: 500 }}>₹{fmt(v)}</Text>;
      },
    },
    {
      title: 'Cash Out', dataIndex: 'amount', width: 120, align: 'right',
      render: (v, row) => row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_OUT
        ? <Text style={{ color: '#cf1322', fontWeight: 500 }}>₹{fmt(v)}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Balance', dataIndex: 'runningBalance', width: 130, align: 'right',
      render: (v) => <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>₹{fmt(v)}</Text>,
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            <WalletOutlined style={{ marginRight: 8 }} />
            Branch Fund Dashboard
          </Title>
          <Space size={8} wrap>
            {branchName && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ShopOutlined style={{ marginRight: 4 }} />{branchName}
              </Text>
            )}
            {statusMeta && (
              <Tag color={statusMeta.color} style={{ marginInlineEnd: 0 }}>{statusMeta.label}</Tag>
            )}
          </Space>
        </Col>
        <Col>
          <Space wrap>
            {isHO && (
              <Select
                placeholder="Select branch"
                allowClear
                showSearch
                style={{ width: 220 }}
                value={selectedBranchId}
                onChange={setSelectedBranchId}
                optionFilterProp="label"
                options={branches.map((b) => ({ value: b._id, label: `${b.name} (${b.code})` }))}
              />
            )}
            <DatePicker value={selectedDate} onChange={setSelectedDate} format="DD/MM/YYYY" allowClear={false} />
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      {isHO && !effectiveBranchId ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a branch to view its fund dashboard"
          />
        </Card>
      ) : (
        <Spin spinning={loading}>
          {/* ── Hero: Cash on Hand ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <Card bordered style={{ background: 'linear-gradient(135deg,#f9f0ff 0%,#f0f5ff 100%)', height: '100%' }}>
                <Statistic
                  title={<span><WalletOutlined /> Cash on Hand</span>}
                  value={fmt(cashOnHand)}
                  prefix="₹"
                  valueStyle={{ fontSize: 30, fontWeight: 'bold', color: cashOnHand < 0 ? '#cf1322' : '#531dab' }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Opening today: ₹{fmt(summary.openingCash)}
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={16}>
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={reversals > 0 ? 6 : 8}>
                  <Card size="small" bordered>
                    <Statistic title={<span><ArrowUpOutlined /> Cash In</span>} value={fmt(summary.totalCashIn)} prefix="₹" valueStyle={{ color: '#389e0d', fontSize: 18 }} />
                  </Card>
                </Col>
                <Col xs={12} sm={reversals > 0 ? 6 : 8}>
                  <Card size="small" bordered>
                    <Statistic title={<span><ArrowDownOutlined /> Cash Out</span>} value={fmt(summary.totalCashOut)} prefix="₹" valueStyle={{ color: '#cf1322', fontSize: 18 }} />
                  </Card>
                </Col>
                {reversals > 0 && (
                  <Col xs={12} sm={6}>
                    <Card size="small" bordered>
                      <Statistic
                        title={<span><RollbackOutlined /> Reversals <Tooltip title="Customer payouts that were reversed — cash returned to the drawer. Shown separately so it never inflates Cash In."><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>}
                        value={fmt(reversals)}
                        prefix="₹"
                        valueStyle={{ color: '#722ed1', fontSize: 18 }}
                      />
                    </Card>
                  </Col>
                )}
                <Col xs={12} sm={reversals > 0 ? 6 : 8}>
                  <Card size="small" bordered>
                    <Statistic
                      title={<span><BankOutlined /> Net Movement</span>}
                      value={fmt(netToday)}
                      prefix="₹"
                      valueStyle={{ color: netToday < 0 ? '#cf1322' : '#262626', fontSize: 18 }}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Cash Drawer Activity — {selectedDate ? selectedDate.format('DD MMM YYYY') : 'today'}
            </Text>
          </Divider>

          <Table
            dataSource={entries}
            columns={columns}
            rowKey="_id"
            loading={loading}
            size="small"
            bordered
            scroll={{ x: 860 }}
            pagination={entries.length > 20 ? { pageSize: 20, showTotal: (t) => `${t} entries`, showSizeChanger: true } : false}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No cash movements on this day" /> }}
          />
        </Spin>
      )}
    </div>
  );
}
